import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { mergeMap, Observable, of, throwError, timer } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';
import { StepPermissions, TeamMember } from '../models/models';

export interface FatDto {
  id: number;
  fatNumber?: string | null;
  statusId?: number | null;
  status: string;
  statusColor?: string | null;
  statusMeta?: string | null;
  workflowId?: number | null;
  workflowName?: string | null;
  currentStepOrder?: number | null;
  projectId?: number | null;
  projectName?: string | null;
  createdAt: string;
  finishedAt?: string | null;
  startedBy?: TeamMember | null;
}

export interface PaginatedFatResponse {
  content: FatDto[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

export interface FatStartRequestDto {
  subcontractorId?: number | null;
  workflowId?: number | null;
  firstFat: boolean;
}

export interface FatFieldPhotoDto {
  id: number;
  fileName: string;
  contentType: string;
  size: number;
}

export interface FatPhotoUploadJobStatusDto {
  jobId: number;
  status: string;
  fileName?: string | null;
  errorMessage?: string | null;
  photo?: FatFieldPhotoDto | null;
}

export interface FatFieldDocumentDto {
  id: number;
  fileName: string;
  contentType: string;
  size: number;
}

export interface FatFieldDto {
  id: number;
  orderId: number;
  name: string;
  result: string | null;
  comment: string | null;
  possibleResults?: string;
  commentMandatory?: boolean;
  commentPlaceholder?: string;
  type?: string;
  minPhotosNbr?: number;
  comptage?: string;
  transformerType?: string;
  includeInReportAnnexes?: boolean;
  photos?: FatFieldPhotoDto[];
  documents?: FatFieldDocumentDto[];
}

export interface FatSubcategoryDto {
  id: number;
  name: string;
  fields: FatFieldDto[];
}

export interface FatCategoryDto {
  id: number;
  name: string;
  subcontractorId?: number | null;
  subcontractorName?: string | null;
  subcategories: FatSubcategoryDto[];
}

export interface FatSignatureDto {
  id: number;
  userId: number;
  firstName: string;
  lastName: string;
  signedAt: string;
  signatureData: string;
  controllerNumber: number;
}

export interface FatDetailDto {
  id: number;
  fatNumber?: string | null;
  statusId?: number | null;
  status: string;
  statusColor?: string | null;
  statusMeta?: string | null;
  workflowId?: number | null;
  workflowName?: string | null;
  currentStepOrder?: number | null;
  createdAt: string;
  finishedAt?: string | null;
  firstFat: boolean;
  categories: FatCategoryDto[];
  signatures?: FatSignatureDto[];
  startedBy?: TeamMember | null;
}

export interface FatExportJobStatusDto {
  jobId: number;
  status: string;
  fileName?: string | null;
  errorMessage?: string | null;
}

@Injectable({ providedIn: 'root' })
export class FatService {
  private http = inject(HttpClient);
  private baseUrl = '/api/projects';

  startFat(projectId: number, payload: FatStartRequestDto): Observable<FatDto> {
    return this.http.post<FatDto>(`${this.baseUrl}/${projectId}/fats/start`, payload);
  }

  getProjectFats(projectId: number): Observable<FatDto[]> {
    return this.http.get<FatDto[]>(`${this.baseUrl}/${projectId}/fats`);
  }

  getAllFats(search: string = '', page: number = 0, size: number = 10, sort: string = 'createdAt,desc'): Observable<PaginatedFatResponse> {
    const params = { search, page: page.toString(), size: size.toString(), sort };
    return this.http.get<PaginatedFatResponse>(`/api/fats`, { params });
  }

  getFatDetail(projectId: number, fatId: number): Observable<FatDetailDto> {
    return this.http.get<FatDetailDto>(`${this.baseUrl}/${projectId}/fats/${fatId}`);
  }

  getWorkflowPermissions(projectId: number, fatId: number): Observable<StepPermissions> {
    return this.http.get<StepPermissions>(`${this.baseUrl}/${projectId}/fats/${fatId}/workflow/permissions`);
  }

  validateWorkflow(projectId: number, fatId: number): Observable<FatDetailDto> {
    return this.http.post<FatDetailDto>(`${this.baseUrl}/${projectId}/fats/${fatId}/workflow/validate`, {});
  }

  rejectWorkflow(projectId: number, fatId: number): Observable<FatDetailDto> {
    return this.http.post<FatDetailDto>(`${this.baseUrl}/${projectId}/fats/${fatId}/workflow/reject`, {});
  }

  resetWorkflow(projectId: number, fatId: number): Observable<FatDetailDto> {
    return this.http.post<FatDetailDto>(`${this.baseUrl}/${projectId}/fats/${fatId}/workflow/reset`, {});
  }

  updateField(projectId: number, fieldId: number, data: Partial<FatFieldDto>): Observable<FatFieldDto> {
    return this.http.patch<FatFieldDto>(`${this.baseUrl}/${projectId}/fats/fields/${fieldId}`, data);
  }

  updateCategory(projectId: number, categoryId: number, data: { subcontractorId?: number | null }): Observable<FatCategoryDto> {
    return this.http.patch<FatCategoryDto>(`${this.baseUrl}/${projectId}/fats/categories/${categoryId}`, data);
  }

  uploadPhoto(projectId: number, fieldId: number, file: File): Observable<FatFieldPhotoDto> {
    const form = new FormData();
    form.append('file', file);
    return this.http.post<FatFieldPhotoDto>(`${this.baseUrl}/${projectId}/fats/fields/${fieldId}/photos`, form);
  }

  uploadPhotoAsync(projectId: number, fieldId: number, file: File): Observable<FatPhotoUploadJobStatusDto> {
    const form = new FormData();
    form.append('file', file);
    return this.http.post<FatPhotoUploadJobStatusDto>(`${this.baseUrl}/${projectId}/fats/fields/${fieldId}/photos/async`, form);
  }

  getPhotoUploadStatus(projectId: number, jobId: number): Observable<FatPhotoUploadJobStatusDto> {
    return this.http.get<FatPhotoUploadJobStatusDto>(`${this.baseUrl}/${projectId}/fats/photo-upload-jobs/${jobId}`);
  }

  waitForPhotoUpload(projectId: number, jobId: number, intervalMs: number = 1000): Observable<FatFieldPhotoDto> {
    return timer(0, intervalMs).pipe(
      switchMap(() => this.getPhotoUploadStatus(projectId, jobId)),
      filter(status => status.status === 'SUCCESS' || status.status === 'FAILED'),
      take(1),
      mergeMap((status) => {
        if (status.status === 'SUCCESS' && status.photo) {
          return of(status.photo);
        }
        return throwError(() => new Error(status.errorMessage || 'Erreur lors du traitement de la photo.'));
      })
    );
  }

  deletePhoto(projectId: number, photoId: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${projectId}/fats/photos/${photoId}`);
  }

  uploadDocument(projectId: number, fieldId: number, file: File): Observable<FatFieldDocumentDto> {
    const form = new FormData();
    form.append('file', file);
    return this.http.post<FatFieldDocumentDto>(`${this.baseUrl}/${projectId}/fats/fields/${fieldId}/documents`, form);
  }

  deleteDocument(projectId: number, documentId: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${projectId}/fats/documents/${documentId}`);
  }

  getDocumentDownload(projectId: number, documentId: number): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/${projectId}/fats/documents/${documentId}/download`, {
      responseType: 'blob'
    });
  }

  getDocumentDownloadUrl(projectId: number, documentId: number): string {
    return `${this.baseUrl}/${projectId}/fats/documents/${documentId}/download`;
  }

  getPhotoPreview(projectId: number, photoId: number): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/${projectId}/fats/photos/${photoId}/preview`, {
      responseType: 'blob'
    });
  }

  getPhotoDownload(projectId: number, photoId: number): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/${projectId}/fats/photos/${photoId}/download`, {
      responseType: 'blob'
    });
  }

  getPhotoPreviewUrl(projectId: number, photoId: number): string {
    return `${this.baseUrl}/${projectId}/fats/photos/${photoId}/preview`;
  }

  getPhotoDownloadUrl(projectId: number, photoId: number): string {
    return `${this.baseUrl}/${projectId}/fats/photos/${photoId}/download`;
  }

  exportNonConformities(projectId: number, fatId: number): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/${projectId}/fats/${fatId}/export-non-conformities`, {
      responseType: 'blob'
    });
  }

  startExportJob(projectId: number, fatId: number): Observable<FatExportJobStatusDto> {
    return this.http.post<FatExportJobStatusDto>(`${this.baseUrl}/${projectId}/fats/${fatId}/export-jobs`, {});
  }

  getExportJobStatus(projectId: number, jobId: number): Observable<FatExportJobStatusDto> {
    return this.http.get<FatExportJobStatusDto>(`${this.baseUrl}/${projectId}/fats/export-jobs/${jobId}`);
  }

  waitForExportJob(projectId: number, jobId: number, intervalMs: number = 1000): Observable<FatExportJobStatusDto> {
    return timer(0, intervalMs).pipe(
      switchMap(() => this.getExportJobStatus(projectId, jobId)),
      filter(status => status.status === 'SUCCESS' || status.status === 'FAILED'),
      take(1),
      mergeMap((status) => {
        if (status.status === 'SUCCESS') {
          return of(status);
        }
        return throwError(() => new Error(status.errorMessage || 'Erreur lors de la génération du PDF.'));
      })
    );
  }

  downloadExportJob(projectId: number, jobId: number): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/${projectId}/fats/export-jobs/${jobId}/download`, {
      responseType: 'blob'
    });
  }

  signFat(projectId: number, fatId: number, controllerNumber: number, signatureData: string): Observable<FatDetailDto> {
    return this.http.post<FatDetailDto>(`${this.baseUrl}/${projectId}/fats/${fatId}/signatures`, signatureData, {
      params: { controllerNumber: controllerNumber.toString() }
    });
  }
}
